import { createContext, useContext, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { refreshToken, fetchProfile } from "../store/authSlice";

const AuthContext = createContext(null);

/**
 * AuthProvider — sits inside <Provider store={store}>.
 *
 * Hydration strategy:
 *   1. authSlice initialState loads user/token from localStorage (instant)
 *   2. On mount, we attempt a silent cookie-based refresh in the background
 *   3. If refresh succeeds → tokens are rotated, localStorage updated
 *   4. If refresh fails but localStorage is still valid → user stays logged in
 *   5. Auto-refresh every 2.5 hours while authenticated (token lasts 3h)
 */
export function AuthProvider({ children }) {
  const dispatch = useDispatch();
  const { isAuthenticated, hydrated } = useSelector((s) => s.auth);
  const intervalRef = useRef(null);
  const didInit = useRef(false);

  // Background refresh on first load — non-blocking
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    dispatch(refreshToken())
      .unwrap()
      .then(() => dispatch(fetchProfile()))
      .catch(() => {
        // If we hydrated from localStorage, user is still logged in.
        // If no cache either, user is a guest — that's fine.
        if (hydrated) {
          // Optionally re-fetch profile with cached token
          dispatch(fetchProfile()).catch(() => {});
        }
      });
  }, [dispatch, hydrated]);

  // Auto-refresh every 2.5 hours while authenticated
  useEffect(() => {
    if (isAuthenticated) {
      intervalRef.current = setInterval(() => {
        dispatch(refreshToken());
      }, 2.5 * 60 * 60 * 1000); // 2h 30m
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isAuthenticated, dispatch]);

  return (
    <AuthContext.Provider value={{ isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
